import Icon from '@/components/shared/icons'
import DateConvert from '@/utils/date-convert'
import { Box, Flex, Heading, Image, Text } from '@chakra-ui/react'
import { API_URL } from '@/services/api/keys'
import { AppDispatch } from '@/store/store'
import { useDispatch } from 'react-redux'
import { ToastConfirm } from '../elements/toast-confirm'
import Toast from '../elements/toast'
import { sessionRefrestUpdate } from '@/store/features/state'
import CustomersEntity from '@/entities/api/customers/customers'
import { deleteCustomerFn } from '@/store/features/customers'
import { removeCustomerById } from '@/services/api/customers/customers'

const CustomerCard = ({
	click,
	customer,
	token,
}: {
	click: () => void
	customer: CustomersEntity
	token: string
}) => {
	const dispatch = useDispatch<AppDispatch>()

	const removeCustomerFn = async (customerToDelete: CustomersEntity) => {
		try {
			ToastConfirm({
				message: '¿Quieres eliminar este cliente?',
				onConfirm: async () => {
					const response = await removeCustomerById(
						token,
						customerToDelete?._id ?? ''
					)

					if (response?.status === 500 || response?.status === 404) {
						Toast({
							type: 'error',
							message: 'No se pudo eliminar el cliente',
						})
						return
					}

					if (response?.status === 401) {
						Toast({
							type: 'info',
							message: 'La sesión del usuario ha expirado',
						})
						dispatch(sessionRefrestUpdate())
						return
					}

					dispatch(deleteCustomerFn(customerToDelete._id ?? ''))
					Toast({
						type: 'success',
						message: 'Cliente eliminado correctamente',
					})
				},
				onCancel: () => {
					Toast({
						type: 'info',
						message: 'Operación cancelada',
					})
				},
				confirmLabel: 'Eliminar',
				cancelLabel: 'Cancelar',
			})
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Error al eliminar el cliente',
			})
		}
	}

	const customerImage = customer.images?.[0]?.url
	const imageUrl = customerImage ? `${API_URL}/${customerImage}` : undefined

	return (
		<Box
			bg='white'
			borderRadius='lg'
			p='4'
			shadow='sm'
			maxW='xs'
			cursor='pointer'
			border='1px solid'
			borderColor='gray.100'
			transition='all 0.2s ease'
			_hover={{
				shadow: 'md',
				borderColor: 'gray.200',
				transform: 'translateY(-2px)',
			}}
		>
			<Flex flexDirection='column' alignItems='center' gap={3}>
				<Box
					w='full'
					display='flex'
					justifyContent='space-between'
					alignItems='center'
				>
					<Text fontSize='xs' fontWeight='400' color='gray.400'>
						{DateConvert(customer._created ?? 'Sin fecha')}
					</Text>
					<Box
						p={1}
						borderRadius='md'
						transition='all 0.2s ease'
						_hover={{
							bg: 'red.50',
							color: 'red.500',
						}}
						onClick={(e) => {
							e.stopPropagation()
							removeCustomerFn(customer)
						}}
					>
						<Icon icon='delete' size='sm' />
					</Box>
				</Box>

				<Box onClick={click} w='full'>
					<Box
						position='relative'
						w='80px'
						h='80px'
						borderRadius='full'
						overflow='hidden'
						shadow='sm'
						mx='auto'
						border='2px solid'
						borderColor='blue.100'
						bg='blue.50'
						mb={3}
					>
						{imageUrl ? (
							<Image
								src={imageUrl}
								alt={`${customer.name} ${customer.lastname}`}
								w='full'
								h='full'
								objectFit='cover'
							/>
						) : (
							<Flex
								w='full'
								h='full'
								alignItems='center'
								justifyContent='center'
								color='blue.400'
							>
								<Icon icon='user' size='xl' />
							</Flex>
						)}
					</Box>

					<Text
						textAlign='center'
						fontSize='xs'
						fontWeight='400'
						color='gray.400'
						mb={1}
					>
						{`Cliente #${customer._id?.slice(-6) ?? 'N/A'}`}
					</Text>

					<Heading
						as='h3'
						fontSize='sm'
						fontWeight='600'
						color='gray.700'
						textAlign='center'
						wordBreak='break-word'
						whiteSpace='normal'
						mb={2}
						lineHeight='short'
					>
						{`${customer.name} ${customer.lastname}`}
					</Heading>

					<Text
						textAlign='center'
						fontSize='xs'
						color='gray.500'
						mb={2}
						wordBreak='break-word'
						whiteSpace='normal'
					>
						{customer.email}
					</Text>
				</Box>
			</Flex>
		</Box>
	)
}

export default CustomerCard
